import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackEvent } from '../utils/consent';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mdd_access_token';

// Global axios interceptor - adds Bearer token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
      }
      setUser(data);
      try { trackEvent('login', { method: 'email' }); } catch { /* ignore */ }
      return { success: true };
    } catch (e) {
      if (!e.response) {
        return { success: false, error: "Network error. Please check your internet connection." };
      }
      const status = e.response.status;
      const detail = e.response.data?.detail;
      if (status === 401) return { success: false, error: detail || "Invalid email or password." };
      if (status === 429) return { success: false, error: "Too many attempts. Please wait a minute and try again." };
      if (status === 422) return { success: false, error: "Please check your email and password format." };
      return { success: false, error: detail || "Something went wrong. Please try again." };
    }
  };

  const register = async (name, email, password, source = null, sourceUrl = null, challengeId = null, challengeAnswer = null, attribution = null) => {
    try {
      const payload = { name, email, password };
      if (source) payload.source = source;
      if (sourceUrl) payload.source_url = sourceUrl;
      if (challengeId) payload.challenge_id = challengeId;
      if (challengeAnswer !== null) payload.challenge_answer = parseInt(challengeAnswer);
      // Attach acquisition attribution captured on first landing
      if (attribution) {
        if (attribution.referrer) payload.referrer = attribution.referrer;
        if (attribution.utm_source) payload.utm_source = attribution.utm_source;
        if (attribution.utm_medium) payload.utm_medium = attribution.utm_medium;
        if (attribution.utm_campaign) payload.utm_campaign = attribution.utm_campaign;
      }
      const { data } = await axios.post(`${API_URL}/api/auth/register`, payload);
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
      }
      setUser(data);
      try {
        trackEvent('CompleteRegistration', {
          status: true,
          source: source || 'direct',
          method: 'email',
        });
      } catch { /* ignore */ }
      return { success: true };
    } catch (e) {
      if (!e.response) {
        return { success: false, error: "Network error. Please check your internet connection." };
      }
      const status = e.response.status;
      const detail = e.response.data?.detail;
      if (status === 400 && detail === "Email already registered") {
        return { success: false, error: "This email is already registered. Please sign in instead." };
      }
      if (status === 429) return { success: false, error: "Too many attempts. Please wait a minute and try again." };
      if (status === 422) {
        if (typeof detail === 'string') return { success: false, error: detail };
        if (Array.isArray(detail)) {
          const msgs = detail.map(e => e.msg).filter(Boolean);
          return { success: false, error: msgs.join('. ') || "Please check your input and try again." };
        }
        return { success: false, error: "Please check your input and try again." };
      }
      return { success: false, error: detail || "Server error. Please try again later." };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(`${API_URL}/api/auth/account`);
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
      return { success: true };
    } catch (e) {
      if (!e.response) {
        return { success: false, error: "Network error. Please check your internet connection." };
      }
      return { success: false, error: e.response.data?.detail || "Failed to delete account." };
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, deleteAccount, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
